import { useEffect, useState, type FormEvent } from "react";
import { useI18n } from "../i18n";
import { addDepartment, loadOrganisation } from "../data";
import type { Branch, Department, Workspace } from "../types";

export function Organisation({ ws }: { ws: Workspace }) {
  const { t, pick } = useI18n();
  const companyId = ws.membership.company_id;
  const canManage = ws.permissions.has("org.manage");
  const [departments, setDepartments] = useState<Department[] | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [parent, setParent] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    try {
      const o = await loadOrganisation(companyId);
      setDepartments(o.departments);
      setBranches(o.branches);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    }
  }
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setError(null);
    try {
      await addDepartment(companyId, name, nameAr, parent || null);
      setName("");
      setNameAr("");
      setParent("");
      setAdding(false);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    }
  }

  const byId = new Map((departments ?? []).map((d) => [d.id, d]));

  return (
    <div className="page">
      <section className="card">
        <div className="card-head">
          <h2>{t("departments")}</h2>
          {canManage && !adding && (
            <button type="button" className="btn primary small" onClick={() => setAdding(true)}>
              {t("addDepartment")}
            </button>
          )}
        </div>
        {adding && (
          <form className="inline-form" onSubmit={submit}>
            <label className="field">
              <span>{t("departmentName")}</span>
              <input id="dept-name" value={name} onChange={(e) => setName(e.target.value)} dir="ltr" autoFocus required />
            </label>
            <label className="field">
              <span>
                {t("departmentNameAr")} <em>{t("optional")}</em>
              </span>
              <input id="dept-name-ar" value={nameAr} onChange={(e) => setNameAr(e.target.value)} dir="rtl" lang="ar" />
            </label>
            <label className="field">
              <span>{t("parentDepartment")}</span>
              <select id="dept-parent" value={parent} onChange={(e) => setParent(e.target.value)}>
                <option value="">{t("none")}</option>
                {(departments ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {pick(d.name, d.name_ar)}
                  </option>
                ))}
              </select>
            </label>
            <div className="actions">
              <button type="button" className="btn secondary" onClick={() => setAdding(false)}>
                {t("cancel")}
              </button>
              <button className="btn primary" disabled={!name.trim()}>
                {t("save")}
              </button>
            </div>
          </form>
        )}
        {error && <p className="alert danger" role="alert">{error}</p>}
        {departments === null ? (
          <p className="muted">{t("loading")}</p>
        ) : departments.length === 0 ? (
          <p className="muted">{t("noDepartments")}</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("departments")}</th>
                  <th>{t("parentDepartment")}</th>
                </tr>
              </thead>
              <tbody>
                {departments.map((d) => {
                  const p = d.parent_id ? byId.get(d.parent_id) : undefined;
                  return (
                    <tr key={d.id}>
                      <td>{pick(d.name, d.name_ar)}</td>
                      <td className="muted">{p ? pick(p.name, p.name_ar) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section className="card">
        <h2>{t("branches")}</h2>
        <ul className="plain-list">
          {branches.map((b) => (
            <li key={b.id}>
              {pick(b.name, b.name_ar)} {b.is_head_office && <span className="badge info">{t("headOffice")}</span>}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
